/**
 * @description Right-side drawer for CMS merchant websites hosting (LYN-76)
 * and Super Admin Elavon merchant-alias edit (companies.merchant_alias).
 *
 * Key features:
 * - Identity summary (business name, account status)
 * - Websites Paid/Unpaid toggle bound to cms_sites.hosting_tier
 * - Disabled + helper when no cmsSiteId (unprovisioned — no auto-provision)
 * - Optimistic toggle with revert on PATCH failure
 * - Elavon alias editor when the open company has Elavon assigned
 * - Visible helper when Elavon is off for this company (do not hide silently)
 *
 * @dependencies
 * - @headlessui/react: Dialog + Transition (EmployeeSlideout pattern)
 * - lucide-react: icons
 * - adminApi.getAdminCompany, adminApi.updateCmsHostingTier
 * - resolveMerchantCompanyId (companyId || id || company_id)
 *
 * @notes
 * - Websites Paid ≠ Payments Paid — this toggle is websites hosting only
 * - Status (Active/Inactive/Unlinked) is account sign-in capability — independent of hosting
 * - Shared by Settings → CMS → Merchants; Tenants → Merchants (LYN-80)
 * - Elavon fetch keys on isOpen + companyId only; hosting-tier updates must
 *   not reset elavonAssigned / merchantAlias or hide the alias editor
 * - GET /admin/companies/:id returning elavonAssigned:false is success, not an
 *   error — Railway/console stay quiet. Surface that gate in the drawer.
 * - companyId is resolved from companyId || id || company_id. A falsy id
 *   skips the fetch entirely (Network stays empty) — show that miss.
 */

import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { getAdminCompany, updateCmsHostingTier } from '../../services/api/adminApi';
import MerchantAliasEditor from './MerchantAliasEditor';
import {
  merchantAccountStatusBadge,
  resolveMerchantAccountStatus,
} from '../../utils/merchantAccountStatus';
import { resolveMerchantCompanyId } from '../../utils/resolveMerchantCompanyId';

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {object|null} props.merchant
 * @param {function} [props.onMerchantUpdated] - called with PATCH response data + companyId
 */
function MerchantDrawer({ isOpen, onClose, merchant, onMerchantUpdated }) {
  const [paid, setPaid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [elavonAssigned, setElavonAssigned] = useState(false);
  const [elavonStatus, setElavonStatus] = useState('idle');
  const [elavonError, setElavonError] = useState(null);
  const [merchantAlias, setMerchantAlias] = useState('');

  const hasCmsSite = Boolean(merchant?.cmsSiteId);
  const companyId = resolveMerchantCompanyId(merchant);
  const hostingTier = merchant?.hostingTier;

  useEffect(() => {
    if (!isOpen || hostingTier === undefined) {
      return;
    }
    setPaid(hostingTier === 'paid');
  }, [isOpen, hostingTier]);

  useEffect(() => {
    if (!isOpen) {
      setElavonAssigned(false);
      setElavonStatus('idle');
      setElavonError(null);
      setMerchantAlias('');
      return undefined;
    }

    if (!companyId) {
      setElavonAssigned(false);
      setElavonStatus('error');
      setElavonError(
        'This merchant row has no company id, so Merchant Alias cannot load.'
      );
      setMerchantAlias('');
      return undefined;
    }

    let cancelled = false;
    setError(null);
    setSaving(false);
    setElavonAssigned(false);
    setElavonStatus('loading');
    setElavonError(null);
    setMerchantAlias('');

    getAdminCompany(companyId)
      .then((result) => {
        if (cancelled) return;
        if (result.success) {
          setElavonAssigned(Boolean(result.data.elavonAssigned));
          setMerchantAlias(result.data.merchantAlias || '');
          setElavonStatus('ready');
          return;
        }
        setElavonAssigned(false);
        setElavonStatus('error');
        setElavonError(result.error?.message || 'Could not load Elavon status for this company.');
      })
      .catch((err) => {
        if (cancelled) return;
        setElavonAssigned(false);
        setElavonStatus('error');
        setElavonError(
          err.response?.data?.error?.message ||
            err.message ||
            'Could not load Elavon status for this company.'
        );
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, companyId]);

  if (!merchant) return null;

  const handleToggle = async () => {
    if (!hasCmsSite || !companyId || saving) return;

    const nextPaid = !paid;
    const previousPaid = paid;
    setPaid(nextPaid);
    setError(null);
    setSaving(true);

    try {
      const result = await updateCmsHostingTier(companyId, {
        hostingTier: nextPaid ? 'paid' : 'free',
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to update hosting tier');
      }
      onMerchantUpdated?.({
        companyId,
        ...result.data,
      });
    } catch (err) {
      setPaid(previousPaid);
      setError(
        err.response?.data?.error?.message ||
          err.message ||
          'Failed to update hosting tier'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-white shadow-2xl">
                    <div className="bg-white px-6 py-6 border-b border-gray-200">
                      <div className="flex items-start justify-between">
                        <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900">
                          Merchant
                        </Dialog.Title>
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          onClick={onClose}
                        >
                          <span className="sr-only">Close panel</span>
                          <X className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </div>

                      <div className="mt-5">
                        <h3 className="text-base font-semibold text-gray-900 truncate">
                          {merchant.businessName}
                        </h3>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {(() => {
                            const status = resolveMerchantAccountStatus(merchant);
                            const badge = merchantAccountStatusBadge(status);
                            return (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                              >
                                {badge.label}
                              </span>
                            );
                          })()}
                          {merchant.slug && (
                            <span className="text-xs text-gray-400 font-mono truncate">
                              {merchant.defaultHostname || merchant.slug}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Websites hosting</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Paid websites entitlement only — independent of payments billing and
                          account status.
                        </p>

                        <div className="mt-4 flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <p className="text-sm text-gray-900">
                              {!hasCmsSite
                                ? 'Not provisioned'
                                : paid
                                  ? 'Paid'
                                  : 'Unpaid (Free)'}
                            </p>
                            {!hasCmsSite && (
                              <p className="text-xs text-amber-600 mt-1">
                                No CMS site provisioned. Provision a site before changing hosting
                                tier.
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={paid}
                            aria-label="Websites paid hosting"
                            disabled={!hasCmsSite || saving}
                            onClick={handleToggle}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                              !hasCmsSite || saving
                                ? 'opacity-50 cursor-not-allowed bg-gray-200'
                                : paid
                                  ? 'bg-indigo-600 cursor-pointer'
                                  : 'bg-gray-200 cursor-pointer'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                                paid ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>

                        {saving && (
                          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Saving…
                          </div>
                        )}

                        {error && (
                          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{error}</span>
                          </div>
                        )}
                      </div>

                      {elavonStatus === 'loading' && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Checking Elavon assignment…
                        </div>
                      )}

                      {elavonStatus === 'error' && elavonError && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{elavonError}</span>
                        </div>
                      )}

                      {elavonStatus === 'ready' && !elavonAssigned && (
                        <p className="text-xs text-gray-500">
                          Merchant Alias is hidden until Elavon is assigned to this
                          company. Enabling Elavon on the tenant or platform
                          backfills merchants that have no other primary gateway.
                        </p>
                      )}

                      {elavonAssigned && (
                        <MerchantAliasEditor
                          companyId={companyId}
                          elavonAssigned={elavonAssigned}
                          initialAlias={merchantAlias}
                          onSaved={(data) => setMerchantAlias(data.merchantAlias || '')}
                        />
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default MerchantDrawer;
